import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TabOfDataTable from '../../../Elements/DataTable/TabOfDataTable';
import OrderDirectionsEnum from '../../../Enums/OrderDirectionsEnum';
import DemoListItem from './DemoListItem';
import DemoListSearchParams from './DemoListSearchParams';
import { demoListServiceFunction, convertDemoItems } from './demoListData';

// Renders children inside an <iframe> so the app's viewport-based responsive CSS
// (@media max-width: 768/640/480 in bravo-app.css) evaluates against the frame's
// own narrow viewport — a plain fixed-width <div> only clips the desktop layout,
// it does not trigger the mobile media queries. Stylesheets + theme are cloned
// from the host document into the frame head so tokens/fonts resolve identically.
function MobileFrame({ width, height, children }) {
  const iframeRef = useRef(null);
  const [mountNode, setMountNode] = useState(null);

  const wireUpFrame = () => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return;
    const doc = iframe.contentDocument;
    if (doc.getElementById('dsg-mobile-root')) return; // already wired

    const theme = document.documentElement.getAttribute('data-theme');
    if (theme) doc.documentElement.setAttribute('data-theme', theme);

    // Inline <style> tags clone directly. For <link> we must copy the RESOLVED
    // absolute href (node.href) — cloneNode keeps the raw "/static/..." path,
    // which won't resolve against the frame's about:blank base in a prod build.
    // getElementsByTagName gives typed HTMLLinkElement/HTMLStyleElement (so .href
    // is available) — querySelectorAll would return bare Element (no .href).
    const links = document.head.getElementsByTagName('link');
    for (let i = 0; i < links.length; i++) {
      if (links[i].rel === 'stylesheet') {
        const link = doc.createElement('link');
        link.rel = 'stylesheet';
        link.href = links[i].href;
        doc.head.appendChild(link);
      }
    }
    const styles = document.head.getElementsByTagName('style');
    for (let i = 0; i < styles.length; i++) {
      doc.head.appendChild(styles[i].cloneNode(true));
    }

    doc.documentElement.style.height = '100%';
    doc.body.style.margin = '0';
    doc.body.style.height = '100%';
    doc.body.style.background = 'var(--bg, #f2f3f5)';

    const root = doc.createElement('div');
    root.id = 'dsg-mobile-root';
    root.className = 'bravo-ds';
    root.style.minHeight = '100%';
    doc.body.appendChild(root);
    setMountNode(root);
  };

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument && iframe.contentDocument.readyState === 'complete') {
      wireUpFrame();
    }
    // about:blank frames fire onLoad; the readyState check above covers the case
    // where load already fired before this effect ran.
  }, []);

  return (
    <>
      <iframe
        ref={iframeRef}
        title='Mobile preview'
        onLoad={wireUpFrame}
        style={{
          display: 'block',
          margin: '0 auto',
          width,
          height,
          border: '1px solid #d6dde5',
          borderRadius: 18,
          boxShadow: '0 2px 12px rgba(16,48,74,0.15)',
          background: '#fff',
        }}
      />
      {mountNode ? createPortal(children, mountNode) : null}
    </>
  );
}

function DemoList({ errorCode }) {
  const [searchItem, setSearchItem] = useState(() => new DemoListSearchParams(true));
  return (
    <TabOfDataTable
      type={DemoListItem}
      serviceFunction={demoListServiceFunction}
      convertDBItemsFunction={convertDemoItems}
      searchItem={searchItem}
      searchParamsType={DemoListSearchParams}
      clickOnStartSearch={(newSearchItem) => setSearchItem(newSearchItem)}
      orderBy='ShopId'
      orderDirection={OrderDirectionsEnum.Asc}
      showSearchStrip={true}
      numberOfElementsOnAPage={10}
      itemTypeName='demo shops'
      enableUrlPush={false}
      errorCode={errorCode}
    />
  );
}

function ListPageDemoComponent() {
  return (
    <div>
      <div className='dsg-row'>
        <div className='dsg-demo-label'>Full width</div>
      </div>
      <DemoList errorCode='DSG201' />

      <div className='dsg-row'>
        <div className='dsg-demo-label'>Mobile width (390px viewport — real @media layout)</div>
      </div>
      <MobileFrame
        width={390}
        height={720}
      >
        <DemoList errorCode='DSG202' />
      </MobileFrame>
    </div>
  );
}

export const listPageDemo = {
  id: 'list-page',
  title: 'List page (TabOfDataTable)',
  category: 'Lists & Grids',
  importPath: 'Elements/DataTable/TabOfDataTable',
  notes: [
    'The full list pattern: search strip + filter panel + sortable grid + detail popup + pagination in one component.',
    'serviceFunction(orderBy, orderDir, pageNumber, numberOfElements, searchParams, clearCache) must resolve to { data, pageNumber, numberOfElementsInTheTable } — this demo serves it from a 30-row in-memory array.',
    'The search-params class extends SearchParamsBase (Elements/SearchParamsBase): fields are set only inside initialize(), a ClearAll() method resets them, and static fieldsInSearchBox drives the filter panel (Select fields take an options array).',
    'enableUrlPush={false} here so the grid does not clobber the gallery’s ?entry= URL param; real pages leave it on for shareable filter URLs.',
    'Construct search params with new X(true) to skip loading filters from the URL.',
    'Click the ID cell to open the detail popup driven by the static FieldsToShowInListTable tabs on the item type.',
    'The row "three-dot" actions menu is an Actions column: { label: "Actions", data: MyItem.getActionsCellContent, type: FieldTypesEnum.ActionButton } where the cell function returns <DataTableActionMenu options={[...]} /> — options take { text, iconName, onClick | url | link, roleKey?, disabled?, hide?, separator? } (see demoRowActions.tsx here, ClientItem.getActionsCellContent for a real example).',
    'Mobile width section: the same list inside a 390px <iframe> so the app’s viewport @media queries actually fire (a fixed-width <div> only clips the desktop grid). At ≤768px rows collapse into stacked cards (headers hidden, labels move into each cell); at ≤640px the toolbar wraps, the count-pill hides and buttons drop their text labels. Caveat: popovers positioned via window.innerWidth (filter flyout, row menu) still measure the host viewport, so they may open off-frame.',
  ],
  snippet:
    "<TabOfDataTable\n  type={MyItem}\n  serviceFunction={MyService.GetListBySearching}\n  convertDBItemsFunction={MyService.ConvertDBItems}\n  searchItem={this.state.searchItem}\n  searchParamsType={MySearchParams}\n  clickOnStartSearch={this.startSearch}\n  orderBy='Id'\n  showSearchStrip={true}\n  itemTypeName='shops'\n/>",
  render: ListPageDemoComponent,
};
